import React, { useState } from "react";

import { ReceivingCondition, Severity } from "node-library/dist/constants";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestExpectationField,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestPackage,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleAdditionalInfoMapping,
	FrontendSampleInstanceAdditionalInfoMapping,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendSampleTestInformation,
	FrontendUserInfo,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import {
	getAdditionalInfoValueDisplayText,
	getReceivingConditionTranslationText,
	getTurnaroundTimeBackgroundColor,
} from "../../../helpers/sampleHelper";

import { Box, Card } from "@mui/material";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import { areStringArraysEqual } from "node-library/dist/helpers/misc_helper";
import { GreenButton, GreyButton } from "../../../common-components/button";
import CreateOrUpdateSpecificationReadingDrawer from "../../../common-components/drawers/createOrUpdateSpecificationReadingDrawer";
import { EditIcon } from "../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { downloadSpecificationFile } from "../../../helpers/frontendFileOperationHelper";
import {
	getBreadcrumbsBySpecFile,
	getSpecificationIcon,
} from "../../../helpers/specificationHelper";
import {
	getEnabledHierarchyNodeTree,
	renderEnabledTestInfoSection,
} from "../../../helpers/testHierarchyHelper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	orderTestCategory: FrontendCompanyTestCategory;

	//basic info
	sampleName: string;
	lotCode: string;
	sampleDescription: string;
	sampleMatrix: FrontendCompanySampleMatrix | undefined;
	sampleAdditionalInfos: FrontendSampleAdditionalInfoMapping[];
	sampleInstanceAdditionalInfos: FrontendSampleInstanceAdditionalInfoMapping[];

	selectedPackage: FrontendCompanyTestPackage | undefined;

	sampleTestInfo: FrontendSampleTestInformation;
	//weight and time
	weight: string;
	weightUnit: string;
	isRetest: boolean;
	isAdditionalTest: boolean;
	treatment: boolean;

	packageReceivedTime: Date;
	expectedCompletionDate: Date;
	receivingCondition: ReceivingCondition;
	selectedSpecificationFile: FrontendSampleSpecification | undefined;
	setSampleSpecifications: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecification[]>
	>;

	otherInstructions: string;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;

	// showInGallery: boolean;
	setCurrentPageIndex: (value: number) => void;
	addOrUpdateSample: () => void;
}

export default function StepSixSampleInfoConfirm(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		user,
		orderTestCategory,
		sampleName,
		lotCode,
		sampleDescription,
		sampleMatrix,
		sampleAdditionalInfos,
		sampleInstanceAdditionalInfos,
		sampleTestInfo,
		selectedPackage,
		weight,
		weightUnit,
		isRetest,
		isAdditionalTest,
		treatment,
		packageReceivedTime,
		expectedCompletionDate,
		receivingCondition,
		selectedSpecificationFile,
		setSampleSpecifications,
		otherInstructions,
		specDirectories,
		// showInGallery,
		setCurrentPageIndex,
		addOrUpdateSample,
	} = props;

	const [readingDrawerOpen, setReadingDrawerOpen] = useState<boolean>(false);
	const hierarchyNodeParentMap = new Map<
		string,
		FrontendCompanyTestHierarchyNode[]
	>();

	orderTestCategory.hierarchyNodes.forEach((node) => {
		const parentId = node.parentNodeId || "root";
		if (!hierarchyNodeParentMap.has(parentId))
			hierarchyNodeParentMap.set(parentId, [node]);
		else {
			hierarchyNodeParentMap.get(parentId)!.push(node);
		}
	});

	const selectedNodeIds: Array<string> = sampleTestInfo.selectedTests.map(
		(test) => {
			return test.selectedNodeId;
		},
	);

	const packageTestNodeIds = selectedPackage?.packageTestNodeIds || [];
	const packageCustomized: boolean =
		packageTestNodeIds.length == 0
			? false
			: !areStringArraysEqual(packageTestNodeIds, selectedNodeIds);
	const handleAddSampleOrder = async (e: React.FormEvent) => {
		e.preventDefault();
		addOrUpdateSample();
	};

	async function handleOpenSpecification() {
		try {
			if (!selectedSpecificationFile) {
				return;
			}

			if (selectedSpecificationFile.isNewSpecification) {
				const result: boolean = await downloadSpecificationFile(
					selectedSpecificationFile,
				);
				if (!result) {
					openSnackbar(
						translation("FailedToDownloadSpecification"),
						Severity.ERROR,
						translation("SystemErrorMsg"),
						<RedErrorIcon />,
					);
				}
			} else {
				setReadingDrawerOpen(true);
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		}
	}
	const breadcrumbs: Array<FrontendSampleSpecificationDirectory> =
		selectedSpecificationFile
			? getBreadcrumbsBySpecFile(selectedSpecificationFile, specDirectories)
			: [];

	const basicInfoCard: React.ReactNode = (
		<Card
			className="common-card card-border-radius-14p"
			style={{
				padding: "15px 25px",
			}}
		>
			<div className="sample-information-grid">
				<Box>
					<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
						<div className="sample-info-tick-icon"></div>
						<div className="typography-heading-03">
							{translation("SampleDetails")}
						</div>
					</div>
				</Box>

				<Box className="overflow-ellipsis-text">
					<div className="sample-information-content ">
						<div className="overflow-ellipsis-text">
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"SampleName",
								)}: `}</div>
								<div className="wrap-text">{sampleName}</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"LotCode",
								)}: `}</div>
								<div className="wrap-text">{lotCode}</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"Matrix",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{sampleMatrix?.subMatrix}
								</div>
							</div>
							{sampleAdditionalInfos.map((info) => {
								return (
									<div className="new-sample-info-row typography-body-01">
										<div className="sample-info-row-description">{`${info.additionalFieldName}: `}</div>
										<div className="overflow-ellipsis-text">
											{getAdditionalInfoValueDisplayText(info)}
										</div>
									</div>
								);
							})}
							{sampleInstanceAdditionalInfos.map((info) => {
								return (
									<div className="new-sample-info-row typography-body-01">
										<div className="sample-info-row-description">{`${info.additionalFieldName}: `}</div>
										<div className="overflow-ellipsis-text">
											{getAdditionalInfoValueDisplayText(info)}
										</div>
									</div>
								);
							})}

							{selectedSpecificationFile && (
								<div className="new-sample-info-row typography-body-01">
									<div className="sample-info-row-description">{`${translation(
										"Specification",
									)}: `}</div>
									<div>
										{getSpecificationIcon(
											selectedSpecificationFile,
											translation,
										)}
									</div>
									<div
										className="overflow-ellipsis-text link-like-text"
										onClick={() => {
											handleOpenSpecification();
										}}
									>
										{`	${[
											...breadcrumbs.map((folder) => {
												return folder.name;
											}),
											`${selectedSpecificationFile.fileName}.${selectedSpecificationFile.format}`,
										].join("/")}`}
									</div>
								</div>
							)}

							{sampleDescription && (
								<div className="new-sample-info-row typography-body-01">
									<div className="sample-info-row-description">{`${translation(
										"SampleDescription",
									)}: `}</div>
									<div className="wrap-text">{sampleDescription}</div>
								</div>
							)}
							{otherInstructions && (
								<div className="new-sample-info-row typography-body-01">
									<div className="sample-info-row-description">{`${translation(
										"OtherInstructions",
									)}: `}</div>
									<div className="wrap-text">{otherInstructions}</div>
								</div>
							)}
						</div>
						<div
							style={{ cursor: "pointer" }}
							onClick={() => {
								setCurrentPageIndex(1);
							}}
						>
							<EditIcon />
						</div>
					</div>
				</Box>
			</div>
		</Card>
	);

	const receptionInfoCard: React.ReactNode = (
		<Card
			className="common-card card-border-radius-14p"
			style={{
				padding: "15px 25px",
			}}
		>
			<div className="sample-information-grid">
				<Box>
					<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
						<div className="sample-info-tick-icon"></div>
						<div className="typography-heading-03">
							{translation("SampleReceptionDetails")}
						</div>
					</div>
				</Box>

				<Box className="overflow-ellipsis-text">
					<div className="sample-information-content ">
						<div className="overflow-ellipsis-text">
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"ReceivedTime",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{formatCustomDate(packageReceivedTime)}
								</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"DueDate",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{formatCustomDate(expectedCompletionDate, true)}
								</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"ReceivingCondition",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{getReceivingConditionTranslationText(
										receivingCondition,
										translation,
									)}
								</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"SampleSize",
								)}: `}</div>
								<div className="overflow-ellipsis-text">{`${weight} ${weightUnit}`}</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"RetestSample",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{isRetest ? translation("Yes") : translation("No")}
								</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"Additionaltest",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{isAdditionalTest ? translation("Yes") : translation("No")}
								</div>
							</div>
							<div className="new-sample-info-row typography-body-01">
								<div className="sample-info-row-description">{`${translation(
									"Treatment",
								)}: `}</div>
								<div className="overflow-ellipsis-text">
									{treatment ? translation("Yes") : translation("No")}
								</div>
							</div>
						</div>
						<div
							style={{ cursor: "pointer" }}
							onClick={() => {
								setCurrentPageIndex(2);
							}}
						>
							<EditIcon />
						</div>
					</div>
				</Box>
			</div>
		</Card>
	);

	const hierarchyTreeRootNode: TestHierarchyTreeNode =
		getEnabledHierarchyNodeTree(selectedNodeIds, hierarchyNodeParentMap);

	const expectationFieldIdToNameMap = new Map<string, string>();
	for (const field of company.testResultAndExpectationFields) {
		const expectationField: FrontendCompanyTestExpectationField | undefined =
			field.expectationField;
		if (!expectationField) {
			continue;
		}
		const fieldId: string = expectationField.expectationFieldId;
		if (field) {
			expectationFieldIdToNameMap.set(fieldId, field.fieldName);
		}
	}

	const fieldsHavingExpectations: FrontendCompanyTestResultField[] =
		company.testResultAndExpectationFields.filter((resultField) => {
			return resultField.expectationField != undefined;
		});

	const testInfoNode: Array<React.ReactNode> = renderEnabledTestInfoSection(
		hierarchyTreeRootNode,
		"typography-body-01",
		fieldsHavingExpectations,
		sampleTestInfo.expectationInputs,
		expectationFieldIdToNameMap,
		company.testResultUnits,
		company.hierarchyNodeMappingPackages,
	);

	const TATBackgroundColor: string = getTurnaroundTimeBackgroundColor(
		sampleTestInfo.turnaroundTime,
	);

	const testInfoList: React.ReactNode = (
		<div
			key={sampleTestInfo.turnaroundTime}
			className="sample-info-test-info-list-item"
		>
			<div className="new-sample-info-row typography-body-01">
				<div className="sample-info-row-description">{`${translation(
					"Turnaroundtime",
				)}: `}</div>
				<div
					className="typography-caption turnaround-time-box"
					style={{ backgroundColor: TATBackgroundColor }}
				>
					{translation(sampleTestInfo.turnaroundTime)}
				</div>
			</div>
			{testInfoNode}
			{sampleTestInfo.additionalTest && (
				<div className="new-sample-info-row typography-body-01">
					<div className="sample-info-row-description">{`${translation(
						"Additionaltests",
					)}: `}</div>
					<div className="wrap-text ">
						{sampleTestInfo.additionalTestDescription}
					</div>
				</div>
			)}
		</div>
	);

	const selectedTestsInfoCard: React.ReactNode = (
		<Card
			className="common-card card-border-radius-14p"
			style={{
				padding: "15px 25px",
			}}
		>
			<div className="sample-information-grid">
				<Box>
					<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
						<div className="sample-info-tick-icon"></div>
						<div className="typography-heading-03">
							{translation("SelectedTests")}
						</div>
					</div>
				</Box>

				<Box>
					<div className="sample-information-content ">
						<div className="overflow-ellipsis-text">
							<div className="typography-body-01 typography-bold overflow-ellipsis-text ">
								{`${selectedPackage?.packageName || translation("Customize")}${
									packageCustomized ? "(" + translation("Customized") + ")" : ""
								}`}
							</div>
							{testInfoList}
						</div>
						<div
							style={{ cursor: "pointer" }}
							onClick={() => {
								setCurrentPageIndex(3);
							}}
						>
							<EditIcon />
						</div>
					</div>
				</Box>
			</div>
		</Card>
	);

	function updateSpecification(specification: FrontendSampleSpecification) {
		setSampleSpecifications((prevSpecs) => {
			// ADD 'return' here
			return prevSpecs.map((spec) => {
				if (spec.specificationId === specification.specificationId) {
					return specification;
				}
				return spec;
			});
		});
	}

	const stepSixSampleInfoComponent: JSX.Element = (
		<div>
			{basicInfoCard}
			{receptionInfoCard}
			{selectedTestsInfoCard}

			<div className="add-sample-form-button-panel">
				<GreyButton
					id="back-button"
					children={translation("Back")}
					onClick={() => setCurrentPageIndex(3)}
				></GreyButton>
				<GreenButton
					id="submit-package-info-button"
					children={translation("Save")}
					type="submit"
					onClick={() => addOrUpdateSample()}
				></GreenButton>
			</div>
			{selectedSpecificationFile && (
				<CreateOrUpdateSpecificationReadingDrawer
					company={company}
					setCompany={setCompany}
					testCategory={orderTestCategory}
					selectedMatrix={sampleMatrix as FrontendCompanySampleMatrix}
					updateSpecification={updateSpecification}
					selectedSpecFile={selectedSpecificationFile}
					user={user}
					open={readingDrawerOpen}
					setOpen={setReadingDrawerOpen}
					specDirectories={specDirectories}
				></CreateOrUpdateSpecificationReadingDrawer>
			)}
		</div>
	);

	return <div style={{ margin: "0 auto" }}>{stepSixSampleInfoComponent}</div>;
}
